import React, { useEffect, useState, useCallback } from 'react';
import {
  RefreshCw, Check, X, CheckSquare, Square, Trash2, ExternalLink,
} from 'lucide-react';
import {
  listInboxItems, approveInboxItem, rejectInboxItem,
  approveInboxBatch, deleteInboxItem, getInboxCounts, getInboxItem,
} from '../services/api';
import type { InboxItem, InboxStatus, InboxCounts } from '../types/v2';

const SOURCE_LABELS: Record<string, string> = {
  upload_document: 'Documento',
  news_api:        'News',
  web_scraping:    'Web',
  rss_feed:        'RSS',
  social_api:      'Social',
};

function tierBadgeClass(score: number): string {
  if (score >= 0.8) return 'bg-green-900/40 text-green-300 border border-green-800/60';
  if (score >= 0.6) return 'bg-teal-900/40 text-teal-300 border border-teal-800/60';
  if (score >= 0.4) return 'bg-yellow-900/40 text-yellow-300 border border-yellow-800/60';
  return 'bg-red-900/40 text-red-300 border border-red-800/60';
}

export function InboxPage() {
  const [activeTab, setActiveTab]         = useState<InboxStatus>('pending');
  const [items, setItems]                 = useState<InboxItem[]>([]);
  const [counts, setCounts]               = useState<InboxCounts>({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading]             = useState(true);
  const [selected, setSelected]           = useState<Set<string>>(new Set());
  const [detail, setDetail]               = useState<InboxItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(async (status: InboxStatus = activeTab) => {
    setLoading(true);
    setSelected(new Set());
    try {
      const [listData, countsData] = await Promise.all([
        listInboxItems({ status, limit: 100 }),
        getInboxCounts(),
      ]);
      setItems(listData.items);
      setCounts(countsData);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(activeTab); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((i) => i.id))
    );
  };

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await approveInboxItem(id);
      await load(activeTab);
      if (detail?.id === id) setDetail(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    setActionLoading(id);
    try {
      await rejectInboxItem(id);
      await load(activeTab);
      if (detail?.id === id) setDetail(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminare questo elemento?')) return;
    setActionLoading(id);
    try {
      await deleteInboxItem(id);
      await load(activeTab);
      if (detail?.id === id) setDetail(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const openDetail = async (item: InboxItem) => {
    if (detail?.id === item.id) {
      setDetail(null);
      return;
    }
    setDetail(item);
    setDetailLoading(true);
    try {
      const full = await getInboxItem(item.id);
      setDetail(full);
    } catch (err) {
      console.error('[InboxPage] failed to load detail content:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBatchApprove = async () => {
    if (selected.size === 0) return;
    setActionLoading('batch');
    try {
      await approveInboxBatch(Array.from(selected));
      await load(activeTab);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const TABS: { status: InboxStatus; label: string }[] = [
    { status: 'pending',  label: `In attesa (${counts.pending})` },
    { status: 'approved', label: `Approvati (${counts.approved})` },
    { status: 'rejected', label: `Rifiutati (${counts.rejected})` },
  ];

  return (
    <div className="flex h-full overflow-hidden">
      {/* Main panel */}
      <div className="flex-1 flex flex-col overflow-hidden p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Inbox — Review fonti</h2>
          <button onClick={() => load(activeTab)} className="text-xs text-gray-400 hover:text-white flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" />
            Aggiorna
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-lg p-1 self-start">
          {TABS.map((tab) => (
            <button
              key={tab.status}
              onClick={() => setActiveTab(tab.status)}
              className={`px-4 py-1.5 text-sm rounded-md font-medium transition-colors ${
                activeTab === tab.status
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Batch toolbar */}
        {activeTab === 'pending' && selected.size > 0 && (
          <div className="flex items-center gap-3 bg-blue-900/30 border border-blue-800 rounded-lg px-4 py-2">
            <span className="text-sm text-blue-300">{selected.size} selezionati</span>
            <button
              onClick={handleBatchApprove}
              disabled={actionLoading === 'batch'}
              className="flex items-center gap-1.5 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded-md transition-colors"
            >
              <Check className="w-3.5 h-3.5" />
              {actionLoading === 'batch' ? 'Approvando...' : 'Approva tutti'}
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-gray-400 hover:text-white transition-colors"
            >
              Deseleziona
            </button>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Caricamento...
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-sm">
            {activeTab === 'pending' ? 'Nessun elemento in attesa. Esegui una ricerca web per aggiungere fonti.' : 'Nessun elemento.'}
          </div>
        ) : (
          <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden overflow-y-auto flex-1">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-900 border-b border-gray-800">
                <tr className="text-xs text-gray-500 uppercase tracking-wide">
                  {activeTab === 'pending' && (
                    <th className="px-4 py-3 w-10">
                      <button onClick={toggleAll}>
                        {selected.size === items.length && items.length > 0
                          ? <CheckSquare className="w-4 h-4 text-blue-400" />
                          : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                  )}
                  <th className="text-left px-4 py-3 font-medium">Titolo</th>
                  <th className="text-left px-4 py-3 font-medium">Sorgente</th>
                  <th className="text-left px-4 py-3 font-medium">Dominio</th>
                  <th className="text-left px-4 py-3 font-medium">Data fetch</th>
                  <th className="px-4 py-3 w-28" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-800/50 cursor-pointer transition-colors ${detail?.id === item.id ? 'bg-gray-800' : ''}`}
                    onClick={() => openDetail(item)}
                  >
                    {activeTab === 'pending' && (
                      <td className="px-4 py-3" onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}>
                        {selected.has(item.id)
                          ? <CheckSquare className="w-4 h-4 text-blue-400" />
                          : <Square className="w-4 h-4 text-gray-600" />}
                      </td>
                    )}
                    <td className="px-4 py-3 text-gray-200 max-w-xs">
                      <span className="line-clamp-1">{item.title}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full">
                        {SOURCE_LABELS[item.source_type] ?? item.source_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{item.source_name ?? '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(item.fetched_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        {activeTab === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(item.id)}
                              disabled={actionLoading === item.id}
                              className="p-1.5 bg-green-800/60 hover:bg-green-700 disabled:opacity-40 text-green-300 rounded transition-colors"
                              title="Approva"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleReject(item.id)}
                              disabled={actionLoading === item.id}
                              className="p-1.5 bg-red-900/60 hover:bg-red-800 disabled:opacity-40 text-red-400 rounded transition-colors"
                              title="Rifiuta"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        {(activeTab === 'rejected') && (
                          <button
                            onClick={() => handleDelete(item.id)}
                            disabled={actionLoading === item.id}
                            className="p-1.5 text-gray-600 hover:text-red-400 disabled:opacity-40 rounded transition-colors"
                            title="Elimina"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail panel */}
      {detail && (
        <aside className="w-[480px] shrink-0 border-l border-gray-800 bg-gray-900 flex flex-col overflow-hidden">
          {/* Header fisso */}
          <div className="flex items-start justify-between gap-2 p-5 border-b border-gray-800 shrink-0">
            <h3 className="text-sm font-semibold text-white leading-snug">{detail.title}</h3>
            <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-white shrink-0 text-lg leading-none">✕</button>
          </div>

          {/* Metadati fissi */}
          <div className="px-5 pt-4 pb-3 border-b border-gray-800 shrink-0 space-y-2">
            {detail.url && (
              <a
                href={detail.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 min-w-0"
              >
                <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{detail.url}</span>
              </a>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
              <span><span className="text-gray-600">Sorgente:</span> {SOURCE_LABELS[detail.source_type] ?? detail.source_type}</span>
              {detail.source_name && <span><span className="text-gray-600">Dominio:</span> {detail.source_name}</span>}
              {detail.source_tier && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${tierBadgeClass(detail.source_tier.score)}`}>
                  {detail.source_tier.label} · {Math.round(detail.source_tier.score * 100)}%
                </span>
              )}
              {detail.author && <span><span className="text-gray-600">Autore:</span> {detail.author}</span>}
              {detail.published_at && <span><span className="text-gray-600">Pubblicato:</span> {new Date(detail.published_at).toLocaleDateString('it-IT')}</span>}
              {detail.content && <span><span className="text-gray-600">Lunghezza:</span> ~{detail.content.length.toLocaleString('it-IT')} caratteri</span>}
            </div>
          </div>

          {/* Contenuto scrollabile */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {detailLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Caricamento contenuto…</span>
              </div>
            ) : (
              <>
                {detail.description && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Descrizione</p>
                    <p className="text-sm text-gray-300 leading-relaxed">{detail.description}</p>
                  </div>
                )}

                {detail.content && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                      Testo completo
                    </p>
                    <p className="text-xs text-gray-300 leading-relaxed whitespace-pre-wrap font-mono bg-gray-950 rounded-lg p-3">
                      {detail.content}
                    </p>
                  </div>
                )}

                {!detail.description && !detail.content && (
                  <p className="text-xs text-gray-600 text-center py-8">Nessun contenuto disponibile</p>
                )}
              </>
            )}
          </div>

          {/* Azioni fisse in basso */}
          <div className="px-5 pb-5 pt-3 border-t border-gray-800 shrink-0 space-y-2">
            {detail.status === 'pending' && (
              <div className="flex gap-2">
                <button
                  onClick={() => handleApprove(detail.id)}
                  disabled={actionLoading === detail.id}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-green-700 hover:bg-green-600 disabled:opacity-50 text-white text-sm py-2 rounded-lg font-medium transition-colors"
                >
                  <Check className="w-4 h-4" />
                  {actionLoading === detail.id ? 'Approvando...' : 'Approva → Pipeline'}
                </button>
                <button
                  onClick={() => handleReject(detail.id)}
                  disabled={actionLoading === detail.id}
                  className="flex-1 flex items-center justify-center gap-1.5 border border-red-700 text-red-400 hover:bg-red-900/30 disabled:opacity-50 text-sm py-2 rounded-lg font-medium transition-colors"
                >
                  <X className="w-4 h-4" />
                  Rifiuta
                </button>
              </div>
            )}
            {detail.status === 'approved' && detail.content_item_id && (
              <div className="text-xs text-green-400 bg-green-900/20 border border-green-800 rounded-lg px-3 py-2">
                Approvato — ContentItem: {detail.content_item_id.slice(0, 8)}...
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
